import { useEffect, useState } from "react";
import api from "@/lib/api";
import { History, Pencil, UserCog, Trash2, Sparkles, CalendarClock, CalendarX2, Layers } from "lucide-react";

/**
 * SuperAdmin-only ledger of every change on a lead.
 * Renders one row per audit entry with icon, action, actor, timestamp, and a diff summary.
 */
const ACTION_META = {
  created:              { icon: Sparkles,     color: "text-indigo-600 bg-indigo-50 border-indigo-200",   label: "Created" },
  updated:              { icon: Pencil,       color: "text-slate-600 bg-slate-50 border-slate-200",       label: "Updated" },
  assigned:             { icon: UserCog,      color: "text-sky-700 bg-sky-50 border-sky-200",             label: "Reassigned" },
  deleted:              { icon: Trash2,       color: "text-rose-700 bg-rose-50 border-rose-200",          label: "Deleted" },
  bulk_updated:         { icon: Layers,       color: "text-amber-700 bg-amber-50 border-amber-200",       label: "Bulk update" },
  meeting_rescheduled:  { icon: CalendarClock,color: "text-violet-700 bg-violet-50 border-violet-200",    label: "Meeting rescheduled" },
  meeting_cancelled:    { icon: CalendarX2,   color: "text-rose-700 bg-rose-50 border-rose-200",          label: "Meeting cancelled" },
};

const fmtValue = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
  if (typeof v === "object") { try { return JSON.stringify(v); } catch { return String(v); } }
  return String(v);
};

export default function AuditLogPanel({ leadId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    if (!leadId) return;
    setLoading(true);
    api.get(`/leads/${leadId}/audit-logs`)
      .then((r) => { if (alive) setLogs(r.data || []); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || "Failed to load audit log"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [leadId]);

  return (
    <div className="solid-panel p-4" data-testid="audit-log-panel">
      <div className="flex items-center gap-2 mb-3">
        <History className="w-4 h-4 text-slate-500" />
        <p className="text-xs text-slate-500 uppercase tracking-widest">Audit log</p>
        <span className="ml-auto text-[11px] text-slate-400">{logs.length}</span>
      </div>

      {loading && <p className="text-xs text-slate-400 italic">Loading…</p>}
      {error && !loading && <p className="text-xs text-rose-500">{error}</p>}
      {!loading && !error && logs.length === 0 && (
        <p className="text-xs text-slate-400 italic">No changes recorded yet.</p>
      )}

      <ol className="space-y-2">
        {logs.map((log) => {
          const meta = ACTION_META[log.action] || ACTION_META.updated;
          const Icon = meta.icon;
          return (
            <li key={log.id} className={`rounded-lg border p-2.5 ${meta.color}`} data-testid={`audit-row-${log.id}`}>
              <div className="flex items-start gap-2">
                <Icon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-[11px] font-semibold uppercase tracking-wider">{meta.label}</span>
                    <span className="text-[11px] text-slate-500">by {log.user_name || "System"}</span>
                    <span className="ml-auto text-[11px] text-slate-400">{new Date(log.created_at).toLocaleString()}</span>
                  </div>
                  {log.summary && <p className="text-xs mt-0.5 text-slate-700">{log.summary}</p>}
                  {log.changes?.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5">
                      {log.changes.map((c, i) => (
                        <li key={i} className="text-[11px] text-slate-600 truncate">
                          <span className="font-medium">{c.field}</span>
                          {": "}
                          <span className="text-slate-400 line-through">{fmtValue(c.from)}</span>
                          <span className="mx-1 text-slate-400">→</span>
                          <span className="text-slate-800">{fmtValue(c.to)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
